import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import { json, redirect, type ActionFunctionArgs, type MetaFunction } from '@remix-run/node'
import { Link, useFetcher } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { P } from '#app/components/typography/p.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Input } from '#app/old_components/ui/input.tsx'
import { Label } from '#app/old_components/ui/label.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

const ForgotPasswordSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = parseWithZod(formData, { schema: ForgotPasswordSchema })
	if (submission.status !== 'success') {
		return json(submission.reply(), { status: submission.status === 'error' ? 400 : 200 })
	}
	const { email } = submission.value

	const user = await prisma.user.findFirstOrThrow({ where: { email }, select: { email: true } })
	if (!user) {
		return json(submission.reply({ formErrors: ['No user exists with this email.'] }))
	}

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'reset-password',
		target: email,
	})

	const response = await sendEmail({
		to: user.email,
		subject: `VerveUI Password Reset`,
		react: <ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})
	if (response.status !== 'success') {
		return json(submission.reply({ formErrors: ['Could not send email. Please try again.'] }))
	}
	return redirect(redirectTo.toString())
}

function ForgotPasswordEmail({ onboardingUrl, otp }: { onboardingUrl: string; otp: string }) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>VerveUI Password Reset</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Password Recovery for VerveUI' }]
}

export default function ForgotPasswordRoute() {
	const forgotPassword = useFetcher<typeof action>()

	const [form, { email }] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container pt-20 pb-32">
			<div className="flex flex-col justify-center">
				<div className="text-center">
					<h1 className="text-h1">Forgot Password</h1>
					<p className="text-body-md text-muted-foreground mt-3">No worries, we'll send you reset instructions.</p>
				</div>
				<div className="mt-16 mx-auto max-w-sm min-w-full sm:min-w-[368px]">
					<forgotPassword.Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<Label htmlFor="email">Email</Label>
						<Input aria-invalid={email.errorId ? true : undefined} aria-describedby={email.errorId} {...getInputProps(email, { type: 'email' })} />
						{email.errors?.map(e => <P key={e}>{e}</P>)}
						<StatusButton
							className="w-full"
							status={forgotPassword.state === 'submitting' ? 'pending' : forgotPassword.data?.status ?? 'idle'}
							type="submit"
							disabled={forgotPassword.state !== 'idle'}
						>
							Recover password
						</StatusButton>
					</forgotPassword.Form>
					<Link to="/login">Back to Login</Link>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
